<script>

    /**
     * @typedef Stitch
     * @property {string} name
     * @property {string} directory
     */

    /**
     * @typedef ResponseData
     * @property {boolean} success
     * @property {Stitch[]} uploaded_stitches
     * @property {string[]} errors
     * @property {string} origin
     *
     * @example
     * ```json
     * {
     *   "success": true,
     *   "uploaded_stitches": [
     *     {
     *       "name": "ProvidersList",
     *       "directory": "/home/common/public_html/tools/saturatron/taco/components/ProvidersList"
     *     }
     *   ],
     *   "errors": [],
     *   "origin": "http://localhost:5173"
     * }
     * ```
     */

    import axios from "axios";
    import {getAbortSignal} from "svelte";
    import {fade, slide} from "svelte/transition";

    let uploading = $state(false);

    /** @type {import("axios").AxiosProgressEvent|null} */
    let uploadProgress = $state(null);

    /** @type {number} */
    let progressPercent = $derived.by(function () {
        return uploadProgress && "progress" in uploadProgress
            ? Math.floor(uploadProgress.progress * 100)
            : 0;
    });

    /** @type {number} */
    let progressBytes = $derived.by(function () {
        return uploadProgress ? uploadProgress.loaded : 0;
    });

    let uploadSuccess = $state(false);

    /** @type {string} */
    let uploadErrorMessage = $state("");

    /** @type {FileList|null} */
    let stitches = $state(null);

    /** @type {Stitch[]} */
    let uploadedStitches = $state([]);

    /** @type {string|null} */
    let validator = $state("");

    /** @type {(HTMLElement & { files?: FileList | [] })|undefined} */
    let fileInputRef = $state();

    $effect(function () {
        validator = new URLSearchParams(window.location.search).get("v");
    });

    $effect(() => {

        if (!stitches || stitches.length === 0) {
            return;
        }

        if (!validator) {
            uploadErrorMessage = "Missing validation parameter.";
            return;
        }

        uploadSuccess = false;
        uploadProgress = null;
        uploading = true;
        uploadErrorMessage = "";

        const formData = new FormData();

        for (const stitch of Array.from(stitches)) {
            formData.append("stitches[]", stitch);
        }

        formData.append("filetype", validator);

        axios.post("https://common.hrstapp.com/tools/saturatron/server/public/acceptor.php",
            formData,
            {
                signal: getAbortSignal(),
                onUploadProgress: progressEvent => uploadProgress = progressEvent
            })
            .then(({data}) => {

                const {success} = /** @type {ResponseData} */ (data);

                uploadSuccess = success === true;
                uploadedStitches = Array.isArray(data?.uploaded_stitches)
                    ? data.uploaded_stitches
                    : [];

                if (fileInputRef) {
                    fileInputRef.files = [];
                }

                stitches = null;

            })
            .catch(error => {
                uploadSuccess = false;
                uploadedStitches = [];
                uploadErrorMessage =
                    error?.response?.data?.error ??
                    error?.message ??
                    "Upload failed.";
            })
            .finally(() => {
                uploading = false;
            });
    });

</script>

<svelte:head>
    <meta charset="UTF-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
    <title>Stitch Uploader</title>
    <script src="https://kit.webawesome.com/e48c4c29c5a648f8.js" crossorigin="anonymous"></script>
</svelte:head>

{#if validator && validator.length > 0}
    <div in:fade style:max-width="120ch" style:margin="10ch auto">
        <header>
            <div class="wa-cluster">
                <wa-icon name="mug-hot"></wa-icon>
                Upload a Stitch for Development
            </div>
        </header>

        <main class="wa-stack">
            <div class="wa-stack">
                <div class="wa-heading-3xl">Stitch Uploader</div>
                <p class="wa-longform-l">
                    This form enables you to upload built stitches to the HRST Stitch Server for developer
                    testing and validation.
                </p>
            </div>

            <div class="wa-stack">
                <wa-file-input
                        accept=".zip"
                        bind:this={fileInputRef}
                        disabled={uploading || null}
                        id="stitches"
                        name="stitches"
                        label="Select a stitch or two"
                        onchange={(/** @type {Event & {currentTarget: HTMLInputElement & { files?: FileList | null }}} */ e) => {
                        stitches = e.currentTarget.files ?? null;
                    }}
                        multiple
                ></wa-file-input>

                {#if uploading || uploadSuccess}
                    <div transition:fade>
                        <wa-progress-bar
                                id="upload-progress"
                                style="--track-height: 1.5rem;"
                                value={progressPercent}>
                            <div style="display: flex;">
                                {progressPercent}%
                                <span style="margin-left: .5rem;">
                        <wa-format-bytes value={progressBytes}></wa-format-bytes>
                    </span>
                            </div>
                        </wa-progress-bar>
                    </div>
                {/if}


                {#if uploadSuccess}
                    <div transition:slide>
                        <wa-callout appearance="filled-outlined" variant="success">
                            <wa-icon slot="icon" name="circle-check" variant="regular"></wa-icon>
                            <div>
                                <strong>Upload Successful</strong>
                            </div>
                            <div class="">
                                {#if uploadedStitches.length > 0}
                                    {#if uploadedStitches.length === 1}
                                        {@const stitch = uploadedStitches[0]}
                                        The <code>{stitch.name}</code> stitch has been uploaded to the Stitch server.
                                        <div>
                                            Location: <code>{stitch.directory}</code>
                                        </div>
                                    {:else}
                                        The following Stitches have been uploaded:
                                        <ol>
                                            {#each uploadedStitches as stitch (stitch.name)}
                                                <li>
                                                    <div>
                                                        <code>{stitch.name}</code> has been uploaded to the HRST Stitch
                                                        Server.
                                                    </div>
                                                    <div>
                                                        Location: <code>{stitch.directory}</code>
                                                    </div>
                                                </li>
                                            {/each}
                                        </ol>
                                    {/if}
                                {/if}
                            </div>
                        </wa-callout>
                    </div>
                {/if}

                {#if uploadErrorMessage}
                    <div transition:slide>
                        <wa-callout variant="danger">
                            <wa-icon slot="icon" name="circle-xmark" variant="regular"></wa-icon>
                            <div>
                                <strong>Upload Failed</strong>
                            </div>
                            <p class="wa-longform-sm">
                                {uploadErrorMessage}
                            </p>
                        </wa-callout>
                    </div>
                {/if}
            </div>
        </main>
    </div>
{/if}

<style>
    wa-progress-bar::part(base) {
        border-radius: var(--wa-border-radius-pill);
    }
</style>